import React, { useState } from "react";
import Slider from "react-slick";
import { motion } from "framer-motion";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Play, Tv, Info } from "lucide-react";
import { Link } from "react-router-dom";
import AnimeModal from "../AnimeModal";
import { getLastEpisode } from "../../services/animeService";

const AnimeSlider = ({ animes }) => {
  const [activeAnime, setActiveAnime] = useState(animes[0]);
  const [showModal, setShowModal] = useState(false);
  const [selectedAnime, setSelectedAnime] = useState(null);
  const [playError, setPlayError] = useState(null);

  const settings = {
    dots: false,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    arrows: true,
    fade: true,
    beforeChange: (_, newIndex) => {
      setActiveAnime(animes[newIndex]);
    },
  };

  const fallbackBackground = "/images/background1.png";

  const backgroundImage = activeAnime.banner
    ? `url(${activeAnime.banner.replace(/\s+/g, "%20")})`
    : `url(${fallbackBackground})`;

  const handlePlayClick = async (anime) => {
    try {
      const response = await getLastEpisode(anime.id);
      if (response.success && response.data) {
        window.location.href = `/animes/${anime.id}?autoplay=true&episodeId=${response.data.id}`;
      } else {
        setSelectedAnime(anime);
        setShowModal(true);
        setPlayError("No se encontraron episodios disponibles para este anime.");
      }
    } catch (error) {
      console.error("Error al obtener el último episodio:", error);
      setSelectedAnime(anime);
      setShowModal(true);
      setPlayError("Error al cargar el episodio. Por favor, intente más tarde.");
    }
  };

  const handleViewSeasonsClick = (anime) => {
    setSelectedAnime(anime);
    setPlayError(null);
    setShowModal(true);
  };

  return (
    <div className="relative w-full overflow-hidden min-h-[90vh]">
      <div
        key={activeAnime.id}
        className="absolute top-0 left-0 w-full h-full bg-cover bg-center"
        style={{
          backgroundImage,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/50 to-transparent"></div>
        <div className="absolute inset-0 bg-black/40"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 h-full flex items-center pt-20 pb-10">
        <Slider {...settings} className="w-full">
          {animes.map((anime) => (
            <div
              key={anime.id}
              className="flex flex-col lg:flex-row items-center gap-8 py-10"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="relative group"
              >
                <img
                  src={anime.poster}
                  alt={anime.name}
                  className="w-[250px] md:w-[300px] lg:w-[350px] h-auto rounded-lg shadow-lg transition-all duration-300 group-hover:shadow-xl"
                />
                {/* {anime.totalSeasons > 0 && (
                  <div className="absolute top-3 left-3 bg-red-600 text-white text-xs px-2 py-1 rounded font-medium">
                    {anime.totalSeasons} {anime.totalSeasons === 1 ? 'Temporada' : 'Temporadas'}
                  </div>
                )} */}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex flex-col gap-4 text-white max-w-lg"
              >
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-shadow">
                  {anime.name}
                </h1>

                <div className="flex flex-wrap items-center gap-3 text-sm md:text-base">
                  <span className="px-2 py-0.5 border border-gray-300 rounded-md">16+</span>
                  {anime.releaseYear && (
                    <span>{anime.releaseYear}</span>
                  )}
                  {anime.totalSeasons > 0 && (
                    <span className="flex items-center gap-1">
                      <Tv size={16} className="inline-block" />
                      {anime.totalSeasons} {anime.totalSeasons === 1 ? 'Temporada' : 'Temporadas'}
                    </span>
                  )}
                  <span className="px-2 py-0.5 border border-gray-300/40 rounded-md text-xs">
                    HD
                  </span>
                </div>

                <div className="flex flex-wrap gap-2">
                  {anime.genre && typeof anime.genre === 'string' && 
                    anime.genre.split(',').map((g, i) => (
                      <span 
                        key={i} 
                        className="text-xs text-gray-300 before:content-['•'] before:mr-2 first:before:content-[''] first:before:mr-0"
                      >
                        {g.trim()}
                      </span>
                    ))
                  }
                </div>

                <p className="text-gray-100 leading-relaxed">
                  {anime.description}
                </p>

                <div className="flex items-center gap-3 mt-6">
                  <button 
                    onClick={() => handlePlayClick(anime)}
                    className="flex items-center px-6 py-3 text-sm font-medium text-black bg-white rounded-md hover:bg-white/90 transition-colors"
                  >
                    <Play className="w-5 h-5 mr-2" fill="black" />
                    Reproducir
                  </button>

                  {anime.totalSeasons > 0 && (
                    <button 
                      onClick={() => handleViewSeasonsClick(anime)}
                      className="flex items-center px-6 py-3 text-sm font-medium text-white bg-gray-800/70 border border-gray-500 rounded-md hover:bg-gray-800/90 transition-colors"
                    >
                      <Info className="w-5 h-5 mr-2" />
                      Más información
                    </button>
                  )}
                </div>
              </motion.div>
            </div>
          ))}
        </Slider>
      </div>

      {showModal && selectedAnime && (
        <AnimeModal 
          anime={selectedAnime} 
          onClose={() => setShowModal(false)} 
          errorMessage={playError}
        />
      )}
    </div>
  );
};

export default AnimeSlider;
